/**
 * High-level React hook for gateway management.
 *
 * Provides a clean API for components to interact with the gateway system:
 * - Add, remove, and focus gateways
 * - Connect all gateways simultaneously
 * - Access connection state and WSClient instances for all gateways
 *
 * All gateways stay connected simultaneously. The "active" gateway is the
 * one that receives text/voice input (the focused gateway). Switching focus
 * does NOT disconnect other gateways.
 *
 * Uses `GatewayContext` internally — must be used within a `GatewayProvider`.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useGatewayStore } from '../gateway/store';
import { GatewayManager } from '../gateway/manager';
import type { GatewayConfig, GatewayStatus } from '../types/gateway';
import type { WSClient } from '../ws/client';

/**
 * Singleton GatewayManager instance.
 */
let _singletonManager: GatewayManager | null = null;
function getSingletonManager(): GatewayManager {
  if (!_singletonManager) {
    _singletonManager = new GatewayManager();
  }
  return _singletonManager;
}

export interface UseGatewayReturn {
  /** All configured gateways */
  gateways: GatewayConfig[];
  /** Currently focused gateway (receives input) — or null */
  activeGateway: GatewayConfig | null;
  /** Connection status of the focused gateway */
  connectionState: GatewayStatus;
  /** Add a new gateway, persist it, and connect */
  addGateway: (url: string, token: string, name?: string) => Promise<GatewayConfig>;
  /** Remove a gateway by ID (disconnects it) */
  removeGateway: (id: string) => void;
  /** Switch focus to a different gateway (does NOT disconnect others) */
  switchGateway: (id: string) => Promise<void>;
  /** Connect all saved gateways simultaneously */
  connectAll: () => Promise<void>;
  /** Reconnect the focused gateway */
  reconnect: () => Promise<void>;
  /** Whether the focused gateway is connected and authenticated */
  isConnected: boolean;
  /** Whether ANY gateway is connected */
  isAnyConnected: boolean;
  /** Whether the focused gateway is waiting for pairing approval */
  isPairing: boolean;
  /** The focused WSClient instance (for chat/voice input) */
  wsClient: WSClient | null;
  /** Get the WSClient for a specific gateway by ID */
  getClient: (gatewayId: string) => WSClient | null;
  /** Get all connected WSClient instances (gatewayId → client) */
  allClients: Map<string, WSClient>;
  /** All connection states keyed by gateway ID */
  connectionStates: Map<string, GatewayStatus>;
  /** Gateway version from the hello-ok handshake (server.version) */
  gatewayVersion: string | null;
  /** Server capabilities for the active gateway from hello-ok caps */
  activeGatewayCaps: string[];
  /** The underlying GatewayManager instance */
  manager: GatewayManager;
}

/**
 * Hook for managing gateway connections.
 *
 * All gateways connect and stay connected simultaneously.
 * The "active" gateway is just the one focused for input.
 */
export function useGateway(): UseGatewayReturn {
  const { state, dispatch, manager: ctxManager } = useGatewayStore();

  const managerRef = useRef(ctxManager ?? getSingletonManager());
  const manager = managerRef.current;

  const { gateways, activeGatewayId, connectionStates, gatewayCaps } = state;

  /** Currently focused gateway config */
  const activeGateway = useMemo(
    () => gateways.find((g) => g.id === activeGatewayId) ?? null,
    [gateways, activeGatewayId],
  );

  /** Connection status of the focused gateway */
  const connectionState: GatewayStatus = useMemo(
    () => (activeGatewayId ? connectionStates.get(activeGatewayId) ?? 'disconnected' : 'disconnected'),
    [activeGatewayId, connectionStates],
  );

  /** Whether the focused gateway is connected */
  const isConnected = connectionState === 'connected';

  /** Whether ANY gateway is connected */
  const isAnyConnected = useMemo(() => {
    for (const [, status] of connectionStates) {
      if (status === 'connected') return true;
    }
    return false;
  }, [connectionStates]);

  /** Whether the focused gateway is waiting for pairing approval */
  const isPairing = connectionState === 'pending';

  /** The focused WSClient instance */
  const wsClient = useMemo(
    () => (activeGatewayId ? manager.getClient(activeGatewayId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeGatewayId, manager, connectionState],
  );

  /** Gateway version from hello-ok (server.version) */
  const gatewayVersion = useMemo(
    () => (activeGatewayId ? manager.getGatewayVersion(activeGatewayId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeGatewayId, manager, connectionState],
  );

  /** Active gateway's server capabilities from hello-ok caps */
  const activeGatewayCaps = useMemo(
    () => (activeGatewayId ? gatewayCaps.get(activeGatewayId) ?? [] : []),
    [activeGatewayId, gatewayCaps],
  );

  /** Get client for a specific gateway */
  const getClient = useCallback(
    (gatewayId: string): WSClient | null => manager.getClient(gatewayId),
    [manager],
  );

  /** All currently connected clients */
  const allClients = useMemo(
    () => manager.getAllClients(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [manager, connectionStates],
  );

  /**
   * Add a new gateway, generate keypair, persist, and initiate connection.
   */
  const addGateway = useCallback(
    async (url: string, token: string, name?: string): Promise<GatewayConfig> => {
      const config = await manager.addGateway(url, token, name);

      dispatch({ type: 'ADD_GATEWAY', gateway: config });

      // Set as active if it's the first gateway
      if (gateways.length === 0) {
        dispatch({ type: 'SET_ACTIVE', id: config.id });
      }

      // Connect (other gateways remain connected)
      await manager.connectGateway(config.id);

      return config;
    },
    [manager, dispatch, gateways.length],
  );

  /**
   * Remove a gateway by ID.
   * Disconnects its WebSocket but leaves other gateways connected.
   */
  const removeGateway = useCallback(
    (id: string) => {
      manager.removeGateway(id);
      dispatch({ type: 'REMOVE_GATEWAY', id });
    },
    [manager, dispatch],
  );

  /**
   * Switch the focused gateway.
   * Does NOT disconnect other gateways — all stay connected.
   */
  const switchGateway = useCallback(
    async (id: string) => {
      dispatch({ type: 'SET_ACTIVE', id });
      await manager.setActiveGateway(id);
    },
    [manager, dispatch],
  );

  /**
   * Connect all saved gateways simultaneously.
   */
  const connectAll = useCallback(async () => {
    await manager.connectAllGateways();
  }, [manager]);

  /**
   * Reconnect the focused gateway.
   */
  const reconnect = useCallback(async () => {
    if (!activeGatewayId) return;
    await manager.connectGateway(activeGatewayId);
  }, [activeGatewayId, manager]);

  // ── Auto-reconnect on visibility change (app resume from background) ──
  // On mobile PWAs the OS throttles/kills timers when backgrounded, so the
  // WebSocket reconnect backoff may never fire. Re-check on resume.
  const gatewaysRef = useRef(gateways);
  gatewaysRef.current = gateways;
  const connectionStatesRef = useRef(connectionStates);
  connectionStatesRef.current = connectionStates;

  useEffect(() => {
    const reconnectDisconnected = () => {
      for (const gw of gatewaysRef.current) {
        const st = connectionStatesRef.current.get(gw.id);
        if (st !== 'connected' && st !== 'connecting') {
          manager.connectGateway(gw.id).catch(() => {});
        }
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        reconnectDisconnected();
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', reconnectDisconnected);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', reconnectDisconnected);
    };
  }, [manager]);

  return {
    gateways,
    activeGateway,
    connectionState,
    addGateway,
    removeGateway,
    switchGateway,
    connectAll,
    reconnect,
    isConnected,
    isAnyConnected,
    isPairing,
    wsClient,
    getClient,
    allClients,
    connectionStates,
    gatewayVersion,
    activeGatewayCaps,
    manager,
  };
}
